import React from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';

/**
 * "This changed somewhere else" - the ONE banner all three editors use.
 *
 * Planning/LIVE-UPDATES-2026-09-03.md, D3 (Script Repository), D4 (TME Wiki)
 * and D9 (Client Wiki). One behaviour, and therefore ONE implementation: three
 * editors meant to behave identically and written three times will drift, and
 * the drift is invisible because each looks right on its own.
 *
 * ⚠️ IT IS THE ONE PLACE IN THIS PLAN THAT IS NOT A SILENT REFRESH, and
 * deliberately so. D1's silent refresh has no way to tell somebody they are
 * about to save over another person's work - so when the editor is DIRTY and
 * the item they have open changed elsewhere, the text is left exactly as typed
 * and this appears above it instead.
 *
 * ⛔ THE ECHO CASE IS THE HOST'S JOB, NOT THIS COMPONENT'S. A save writes a row,
 * the row streams, and the signal comes back naming the item the saver still has
 * open about a second later - so each host GETs the item and compares its
 * version with the one it holds before mounting this at all. "Equal" means "no
 * change this editor needs to know about", NOT "our own write": on the Script
 * Repository a metadata-only edit by another admin also compares equal, and that
 * is correct, because this banner protects the editor BODY and the list
 * refreshes regardless.
 *
 * ⚠️ NO `title` ATTRIBUTE ANYWHERE IN HERE. Native browser tooltips are banned
 * project-wide; if this ever needs a hover explanation it uses `Tooltip` /
 * `BUBBLE_CLASSES`, never a fourth bubble renderer.
 *
 * @param {string}   message  what changed, in the host's own words
 * @param {function} onReload discard local edits and reload from the server
 * @param {string}   confirm  the unsaved-changes prompt to put up first. The
 *                            host passes its OWN existing wording - reuse it,
 *                            do not write a second prompt.
 * @param {boolean}  busy     a save or reload already in flight
 */
const StaleContentBanner = ({ message, onReload, confirm, busy = false }) => {
    // ⚠️ No `if (busy) return` here: the button below is `disabled` while busy,
    // so that line could never run - and an unreachable guard on a file pinned
    // at 100% coverage is a line nobody can ever make green.
    const handleReload = () => {
        if (confirm && !window.confirm(confirm)) return;
        onReload();
    };

    return (
        <div
            role="status"
            data-testid="stale-content-banner"
            className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
            <span className="flex items-center gap-2">
                <FaExclamationTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                {message}
            </span>
            <button
                type="button"
                onClick={handleReload}
                disabled={busy}
                className="px-3 py-1 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 disabled:opacity-50"
            >
                Reload
            </button>
        </div>
    );
};

export default StaleContentBanner;
